// Renderer — putar manifest dari blob IndexedDB cache. Fallback ke network URL
// kalau cache miss (mis. user offline saat first push).

import { getCachedMedia } from "./media-cache.js";
import { renderPdfSlideshow } from "./pdf-renderer.js";

interface ManifestMedia {
  id: string;
  name: string;
  media_type: "image" | "video" | "audio" | "document" | "url";
  url: string;
  thumbnail_url: string | null;
  mime_type: string | null;
  duration: number;
  width: number | null;
  height: number | null;
  rotation?: number;
  object_fit?: string;
  availability_start?: string | null;
  availability_end?: string | null;
}

/** CSS untuk elemen media: object-fit (crop/scaling) + rotate (appearance). */
function mediaStyle(media: ManifestMedia): string {
  const fit = media.object_fit ?? "contain";
  const rot = media.rotation ?? 0;
  const transform = rot ? `transform:rotate(${rot}deg);` : "";
  return `width:100%; height:100%; object-fit:${fit}; ${transform}background:#000;`;
}

/** True kalau media sedang DALAM window availability (atau tak punya window). */
function isAvailableNow(media: ManifestMedia | null | undefined): boolean {
  if (!media) return true;
  const now = Date.now();
  if (media.availability_start && now < new Date(media.availability_start).getTime())
    return false;
  if (media.availability_end && now > new Date(media.availability_end).getTime())
    return false;
  return true;
}

interface ManifestPlaylistItem {
  id: string;
  order_index: number;
  duration: number;
  content_type?: "media" | "playlist" | "layout" | "app";
  media: ManifestMedia | null;
  // Layout sebagai item playlist (resolved oleh backend). null untuk non-layout.
  layout?: ManifestLayoutData | null;
}

interface ManifestPlaylistData {
  id: string;
  name: string;
  version: number;
  image_transition: string;
  sync_playback: boolean;
  random_playback: boolean;
  items: ManifestPlaylistItem[];
}

interface ManifestLayoutZone {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  content:
    | { kind: "media"; media: ManifestMedia }
    | { kind: "playlist"; playlist: ManifestPlaylistData }
    | { kind: "app"; app: { id: string; label: string; url: string } }
    | null;
}

interface ManifestLayoutData {
  id: string;
  name: string;
  version: number;
  resolution: { width: number; height: number };
  bg_color: string;
  zones: ManifestLayoutZone[];
}

export type Manifest =
  | {
      push_id: string;
      pushed_at: string;
      // Opsional: cache lama mungkin belum punya field ini. true = paket Free.
      watermark?: boolean;
      type: "media";
      media: ManifestMedia;
    }
  | {
      push_id: string;
      pushed_at: string;
      watermark?: boolean;
      type: "playlist";
      playlist: ManifestPlaylistData;
    }
  | {
      push_id: string;
      pushed_at: string;
      watermark?: boolean;
      type: "layout";
      layout: ManifestLayoutData;
    };

// =========================================================================
// State
// =========================================================================

interface RenderState {
  manifest: Manifest | null;
  itemIndex: number;
  timer: ReturnType<typeof setTimeout> | null;
  currentObjectUrl: string | null;
  /** Cleanup function untuk PDF slideshow yang sedang aktif (kalau ada). */
  pdfCleanup: (() => void) | null;
  /** Cleanup functions untuk zones di layout mode (kalau ada). */
  layoutCleanups: Array<() => void>;
}

const state: RenderState = {
  manifest: null,
  itemIndex: 0,
  timer: null,
  currentObjectUrl: null,
  pdfCleanup: null,
  layoutCleanups: [],
};
let rootEl: HTMLElement | null = null;

// =========================================================================
// Public API
// =========================================================================

export function attachRenderer(root: HTMLElement) {
  rootEl = root;
}

// =========================================================================
// Watermark branding — hanya untuk paket Free. Overlay fixed di pojok,
// di atas semua konten (termasuk layout zones). pointer-events none.
// =========================================================================

const WATERMARK_ID = "signage-watermark";

function applyWatermark(show: boolean) {
  const existing = document.getElementById(WATERMARK_ID);
  if (!show) {
    existing?.remove();
    return;
  }
  if (existing) return;
  const el = document.createElement("div");
  el.id = WATERMARK_ID;
  el.setAttribute(
    "style",
    [
      "position:fixed",
      "right:16px",
      "bottom:16px",
      "z-index:2147483000",
      "pointer-events:none",
      "display:flex",
      "align-items:center",
      "gap:6px",
      "padding:6px 12px",
      "border-radius:9999px",
      "background:rgba(0,0,0,0.55)",
      "backdrop-filter:blur(4px)",
      "color:#fff",
      "font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif",
      "font-size:13px",
      "font-weight:700",
      "letter-spacing:0.02em",
      "box-shadow:0 2px 8px rgba(0,0,0,0.3)",
      "user-select:none",
    ].join(";"),
  );
  el.innerHTML =
    '<span style="display:inline-block;width:14px;height:14px;border-radius:4px;background:linear-gradient(135deg,#ff4b4b,#b4141b)"></span>' +
    "<span>NETBIZ Signage</span>";
  document.body.appendChild(el);
}

export function setManifest(manifest: Manifest | null) {
  const normalized = manifest ? rewriteManifestUrls(manifest) : null;
  state.manifest = normalized;
  state.itemIndex = 0;
  // Watermark branding untuk paket Free (paket berbayar = tanpa watermark).
  applyWatermark(normalized ? normalized.watermark === true : false);
  clearTimer();
  revokeCurrentBlobUrl();
  if (state.pdfCleanup) {
    state.pdfCleanup();
    state.pdfCleanup = null;
  }
  // Cleanup semua zone layout sebelumnya kalau ada.
  cleanupLayoutZones();

  if (!normalized) {
    showIdle();
    return;
  }
  if (normalized.type === "layout") {
    renderLayout(normalized.layout);
    return;
  }
  void renderCurrent();
}

export function showLoading(message: string) {
  if (!rootEl) return;
  rootEl.innerHTML = `
    <div class="player-placeholder">
      <h1>${escapeHtml(message)}</h1>
    </div>
  `;
}

export function showLoadingProgress(completed: number, total: number, name?: string) {
  if (!rootEl) return;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  rootEl.innerHTML = `
    <div class="player-placeholder">
      <h1>Menyiapkan konten</h1>
      <p>${completed} / ${total} (${pct}%)</p>
      ${name ? `<p style="margin-top:0.5rem; opacity:0.7;">${escapeHtml(name)}</p>` : ""}
      <div style="width:min(60vw, 400px); height:4px; background:#222; border-radius:2px; margin:1rem auto 0; overflow:hidden;">
        <div style="width:${pct}%; height:100%; background:#3b82f6; transition:width 0.3s;"></div>
      </div>
    </div>
  `;
}

// =========================================================================
// URL rewriting (LAN access — localhost di server -> hostname player saat ini)
// =========================================================================

function rewriteUrl(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") {
      u.hostname = window.location.hostname;
    }
    return u.toString();
  } catch {
    return url;
  }
}

function rewriteMedia(m: ManifestMedia): ManifestMedia {
  return {
    ...m,
    url: rewriteUrl(m.url),
    thumbnail_url: m.thumbnail_url ? rewriteUrl(m.thumbnail_url) : null,
  };
}

// Rewrite semua URL di dalam satu layout (zona media/playlist/app). Dipakai
// untuk layout top-level maupun layout sebagai item playlist.
function rewriteLayoutData(layout: ManifestLayoutData): ManifestLayoutData {
  return {
    ...layout,
    zones: layout.zones.map((z) => {
      if (!z.content) return z;
      if (z.content.kind === "media") {
        return { ...z, content: { kind: "media", media: rewriteMedia(z.content.media) } };
      }
      if (z.content.kind === "playlist") {
        return {
          ...z,
          content: {
            kind: "playlist",
            playlist: {
              ...z.content.playlist,
              items: z.content.playlist.items.map((it) => ({
                ...it,
                media: it.media ? rewriteMedia(it.media) : null,
              })),
            },
          },
        };
      }
      if (z.content.kind === "app") {
        return {
          ...z,
          content: {
            kind: "app",
            app: {
              ...z.content.app,
              url: z.content.app.url ? rewriteUrl(z.content.app.url) : "",
            },
          },
        };
      }
      return z;
    }),
  };
}

function rewriteManifestUrls(manifest: Manifest): Manifest {
  if (manifest.type === "media") {
    return { ...manifest, media: rewriteMedia(manifest.media) };
  }
  if (manifest.type === "playlist") {
    return {
      ...manifest,
      playlist: {
        ...manifest.playlist,
        items: manifest.playlist.items.map((it) => ({
          ...it,
          media: it.media ? rewriteMedia(it.media) : null,
          layout: it.layout ? rewriteLayoutData(it.layout) : it.layout,
        })),
      },
    };
  }
  // layout
  return { ...manifest, layout: rewriteLayoutData(manifest.layout) };
}

// =========================================================================
// Render loop
// =========================================================================

function showIdle() {
  if (!rootEl) return;
  rootEl.innerHTML = `
    <div class="player-placeholder">
      <h1>Menunggu konten</h1>
      <p>Buka dashboard, klik <strong>Push</strong> ke layar ini untuk mulai.</p>
    </div>
  `;
}

function clearTimer() {
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
}

// Teardown semua zone layout yang sedang aktif (timer/blob/iframe per zona).
function cleanupLayoutZones() {
  for (const cleanup of state.layoutCleanups) {
    try {
      cleanup();
    } catch (err) {
      console.warn("[layout] zone cleanup error:", err);
    }
  }
  state.layoutCleanups = [];
}

function revokeCurrentBlobUrl() {
  if (state.currentObjectUrl) {
    URL.revokeObjectURL(state.currentObjectUrl);
    state.currentObjectUrl = null;
  }
}

async function renderCurrent() {
  if (!rootEl || !state.manifest) return;

  // Layout di-handle terpisah di setManifest → renderLayout; renderCurrent
  // hanya untuk media/playlist linear playback.
  if (state.manifest.type === "layout") return;

  let item: { duration: number; media: ManifestMedia | null; layout?: ManifestLayoutData | null };
  if (state.manifest.type === "media") {
    const m = state.manifest.media;
    // Availability: di luar window → idle, cek ulang 60 detik lagi.
    if (!isAvailableNow(m)) {
      clearTimer();
      showIdle();
      state.timer = setTimeout(() => void renderCurrent(), 60000);
      return;
    }
    item = { duration: m.duration, media: m };
  } else {
    const items = state.manifest.playlist.items;
    if (items.length === 0) {
      showIdle();
      return;
    }
    // Cari item renderable (media available ATAU layout) mulai dari itemIndex.
    // Item lain (mis. app yang belum punya render) dilewati.
    const renderable = (it: ManifestPlaylistItem) =>
      (it.media && isAvailableNow(it.media)) || !!it.layout;
    let found: ManifestPlaylistItem | null = null;
    for (let i = 0; i < items.length; i++) {
      const cand = items[(state.itemIndex + i) % items.length]!;
      if (renderable(cand)) {
        state.itemIndex = (state.itemIndex + i) % items.length;
        found = cand;
        break;
      }
    }
    // Tidak ada item renderable → idle, cek ulang 60 detik.
    if (!found) {
      clearTimer();
      showIdle();
      state.timer = setTimeout(() => void renderCurrent(), 60000);
      return;
    }
    item = found;
  }

  // Bersihkan zona layout dari slot sebelumnya (kalau item sebelumnya layout).
  cleanupLayoutZones();

  // Item layout (di dalam playlist) → render layout penuh untuk durasinya.
  if (item.layout) {
    renderLayout(item.layout);
    clearTimer();
    if (state.manifest.type === "playlist") {
      state.timer = setTimeout(advance, Math.max(1000, item.duration * 1000));
    }
    return;
  }

  if (!item.media) {
    advance();
    return;
  }

  await renderMedia(item.media);

  // Timer hanya untuk playlist advance ke item berikutnya. Single-media TIDAK
  // butuh timer karena:
  // - image: static, tampil selamanya sampai push baru.
  // - video: <video loop muted autoplay> sudah loop sendiri secara native.
  // - PDF document: renderPdfSlideshow self-cycle antar halaman (loop internal).
  // - iframe (YouTube/Vimeo/widget): iframe self-managed.
  // Outer timer di single-media JUSTRU bug — re-render = re-fetch full asset
  // & restart dari awal (mis. PDF balik ke page 1 + reload network).
  const shouldAdvance =
    state.manifest.type === "playlist" && state.manifest.playlist.items.length > 0;

  clearTimer();
  if (shouldAdvance) {
    state.timer = setTimeout(advance, Math.max(1000, item.duration * 1000));
  }
}

function advance() {
  if (!state.manifest) return;
  if (state.manifest.type === "playlist") {
    const items = state.manifest.playlist.items;
    state.itemIndex = (state.itemIndex + 1) % Math.max(1, items.length);
    void renderCurrent();
  } else {
    void renderCurrent();
  }
}

// =========================================================================
// Render single media — prefer cache (blob URL), fallback ke network URL
// =========================================================================

function isYoutubeEmbed(url: string): boolean {
  return /youtube\.com\/embed\/|youtu\.be\//i.test(url);
}

function isVimeoEmbed(url: string): boolean {
  return /player\.vimeo\.com\/video\//i.test(url);
}

async function resolveMediaSrc(media: ManifestMedia): Promise<string> {
  // Cek IndexedDB cache. Kalau ada, pakai object URL (instant + offline ok).
  const cached = await getCachedMedia(media.id);
  if (cached) {
    return URL.createObjectURL(cached.blob);
  }
  // Fallback: pakai URL network (browser akan fetch tiap render, slow tapi works).
  return media.url;
}

async function renderMedia(media: ManifestMedia) {
  if (!rootEl) return;

  // Revoke object URL sebelumnya kalau ada (free memory).
  revokeCurrentBlobUrl();
  // Cleanup PDF slideshow sebelumnya kalau ada.
  if (state.pdfCleanup) {
    state.pdfCleanup();
    state.pdfCleanup = null;
  }

  // Type='url' (web page generic): render iframe.
  // Plus catch-all: kalau media punya URL embed YouTube/Vimeo (walaupun type
  // di-set 'video' karena user filter UX) → tetap render iframe, bukan
  // <video> element (embed URL bukan file video).
  const isEmbedUrl =
    media.media_type === "url" ||
    isYoutubeEmbed(media.url) ||
    isVimeoEmbed(media.url) ||
    media.mime_type === "application/x-youtube" ||
    media.mime_type === "application/x-vimeo" ||
    media.mime_type === "application/x-generic_iframe";
  if (isEmbedUrl) {
    return renderIframe(media);
  }

  const src = await resolveMediaSrc(media);
  if (src.startsWith("blob:")) state.currentObjectUrl = src;

  // Document → render via pdf.js (backend sudah convert PPT/DOC/XLSX → PDF).
  // Detect PDF dari mime atau URL extension supaya fleksibel.
  if (
    media.media_type === "document" &&
    (media.mime_type === "application/pdf" ||
      /\.pdf(\?|$)/i.test(media.url))
  ) {
    rootEl.innerHTML = "";
    state.pdfCleanup = await renderPdfSlideshow({
      container: rootEl,
      pdfUrl: src,
      totalDurationSec: Math.max(5, media.duration),
    });
    return;
  }

  if (media.media_type === "image") {
    const img = new Image();
    img.onerror = () => {
      if (!rootEl) return;
      rootEl.innerHTML = `
        <div class="player-placeholder">
          <h1>Gagal memuat gambar</h1>
          <p>${escapeHtml(media.name)}</p>
        </div>
      `;
    };
    img.onload = () => {
      if (!rootEl) return;
      rootEl.innerHTML = "";
      img.style.cssText =
        mediaStyle(media);
      img.alt = media.name;
      rootEl.appendChild(img);
    };
    img.src = src;
    return;
  }

  if (media.media_type === "video") {
    const v = document.createElement("video");
    v.src = src;
    v.autoplay = true;
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.style.cssText = mediaStyle(media);
    rootEl.innerHTML = "";
    rootEl.appendChild(v);
    return;
  }

  rootEl.innerHTML = `
    <div class="player-placeholder">
      <h1>${escapeHtml(media.name)}</h1>
      <p>Tipe ${media.media_type} belum didukung di MVP.</p>
    </div>
  `;
}

/**
 * Render media type='url' sebagai iframe fullscreen.
 * YouTube/Vimeo embed URL sudah include autoplay, mute, loop params dari backend.
 * Generic URL (iframe ke web page lain) juga via iframe ini.
 *
 * Audio note: untuk YouTube auto-play di browser modern, mute=1 wajib di URL.
 * Suara baru bisa di-unmute via interaksi user (out of scope untuk kiosk signage).
 */
function renderIframe(media: ManifestMedia) {
  if (!rootEl) return;
  const iframe = document.createElement("iframe");
  iframe.src = media.url;
  iframe.title = media.name;
  // Allow autoplay + fullscreen + encrypted-media untuk YouTube/Vimeo.
  iframe.allow =
    "autoplay; fullscreen; picture-in-picture; accelerometer; gyroscope; encrypted-media";
  // sandbox dilonggarkan supaya YouTube player bisa kerja (no allow-top-navigation).
  // Untuk generic page kiosk, mungkin perlu loosen lagi (allow-forms, allow-modals).
  iframe.setAttribute(
    "sandbox",
    "allow-scripts allow-same-origin allow-presentation allow-popups",
  );
  iframe.referrerPolicy = "no-referrer-when-downgrade";
  iframe.style.cssText =
    "width:100%; height:100%; border:0; background:#000; display:block;";
  rootEl.innerHTML = "";
  rootEl.appendChild(iframe);
}

// =========================================================================
// Layout renderer — bagi screen jadi N zona, tiap zona play konten sendiri.
// =========================================================================

function renderLayout(layout: ManifestLayoutData) {
  if (!rootEl) return;
  rootEl.innerHTML = "";
  rootEl.style.cssText = "position:relative; width:100%; height:100%; background:#000;";

  // Container layout — full screen, bg color dari config.
  const container = document.createElement("div");
  container.style.cssText = `
    position: absolute;
    inset: 0;
    background: ${layout.bg_color || "#0F172A"};
    overflow: hidden;
  `;
  rootEl.appendChild(container);

  // Render tiap zona.
  for (const zone of layout.zones) {
    const zoneEl = document.createElement("div");
    zoneEl.dataset.zoneId = zone.id;
    zoneEl.dataset.zoneName = zone.name;
    zoneEl.style.cssText = `
      position: absolute;
      left: ${zone.x}%;
      top: ${zone.y}%;
      width: ${zone.width}%;
      height: ${zone.height}%;
      overflow: hidden;
      background: #000;
    `;
    container.appendChild(zoneEl);
    const cleanup = renderZoneContent(zoneEl, zone);
    if (cleanup) state.layoutCleanups.push(cleanup);
  }
}

/**
 * Render konten satu zona. Return cleanup function untuk teardown timer/blob/dll.
 *
 * - media   → image / video / pdf / iframe (single, no advance)
 * - playlist → mini cycler: ganti item per duration, looping
 * - app     → kalau ada URL widget, render iframe. Kalau tidak, placeholder.
 * - null    → placeholder "Zone Name" (kosong, cuma kotak)
 */
function renderZoneContent(
  container: HTMLElement,
  zone: ManifestLayoutZone,
): (() => void) | null {
  if (!zone.content) {
    container.innerHTML = `
      <div style="display:flex; align-items:center; justify-content:center; height:100%; color:#666; font-size:12px; font-family:system-ui; text-align:center; padding:8px;">
        <div>
          <div style="opacity:.5; font-size:10px; text-transform:uppercase; letter-spacing:.1em;">Zona kosong</div>
          <div style="margin-top:4px; font-weight:600;">${escapeHtml(zone.name)}</div>
        </div>
      </div>
    `;
    return null;
  }

  if (zone.content.kind === "media") {
    return renderZoneMedia(container, zone.content.media);
  }

  if (zone.content.kind === "playlist") {
    return renderZonePlaylist(container, zone.content.playlist);
  }

  if (zone.content.kind === "app") {
    // App di-render via iframe ke URL widget. Kalau URL kosong (mis. app
    // instance yang config-nya di-store di zustand dashboard), tampil
    // placeholder. Solusi proper: dashboard saat assign zone harus pass
    // URL widget lengkap (Prayer Times: /prayer-times.html?city=...&...).
    if (zone.content.app.url) {
      const iframe = document.createElement("iframe");
      iframe.src = zone.content.app.url;
      iframe.title = zone.content.app.label;
      iframe.allow = "autoplay; fullscreen; picture-in-picture";
      iframe.setAttribute("sandbox", "allow-scripts allow-same-origin");
      iframe.style.cssText = "width:100%; height:100%; border:0; background:#000; display:block;";
      container.appendChild(iframe);
      return () => {
        try {
          iframe.remove();
        } catch {
          // ignore
        }
      };
    }
    container.innerHTML = `
      <div style="display:flex; align-items:center; justify-content:center; height:100%; color:#fff; font-size:12px; font-family:system-ui; text-align:center; padding:8px; background:linear-gradient(135deg,#7c3aed,#4c1d95);">
        <div>
          <div style="font-size:24px; margin-bottom:8px;">🅐</div>
          <div style="font-weight:700;">${escapeHtml(zone.content.app.label)}</div>
          <div style="margin-top:4px; opacity:.7; font-size:10px;">App widget (player belum punya URL render)</div>
        </div>
      </div>
    `;
    return null;
  }

  return null;
}

function renderZoneMedia(
  container: HTMLElement,
  media: ManifestMedia,
): () => void {
  let objectUrl: string | null = null;
  let cancelled = false;
  let pdfCleanup: (() => void) | null = null;

  void (async () => {
    if (cancelled || !container.isConnected) return;

    // type='url' → iframe (YouTube/Vimeo/web page)
    // Same logic dengan top-level renderMedia: render iframe untuk URL type
    // + catch-all embed URL (YouTube/Vimeo) walaupun type-nya 'video'.
    const isEmbedUrl =
      media.media_type === "url" ||
      isYoutubeEmbed(media.url) ||
      isVimeoEmbed(media.url) ||
      media.mime_type === "application/x-youtube" ||
      media.mime_type === "application/x-vimeo" ||
      media.mime_type === "application/x-generic_iframe";
    if (isEmbedUrl) {
      const iframe = document.createElement("iframe");
      iframe.src = media.url;
      iframe.title = media.name;
      iframe.allow = "autoplay; fullscreen; picture-in-picture";
      iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-presentation");
      iframe.style.cssText = "width:100%; height:100%; border:0; background:#000; display:block;";
      container.appendChild(iframe);
      return;
    }

    // Resolve cached blob URL atau fallback ke network URL.
    const cached = await getCachedMedia(media.id);
    const src = cached ? URL.createObjectURL(cached.blob) : media.url;
    if (src.startsWith("blob:")) objectUrl = src;

    if (cancelled) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      return;
    }

    // PDF document → slideshow
    if (
      media.media_type === "document" &&
      (media.mime_type === "application/pdf" ||
        /\.pdf(\?|$)/i.test(media.url))
    ) {
      pdfCleanup = await renderPdfSlideshow({
        container,
        pdfUrl: src,
        totalDurationSec: Math.max(10, media.duration),
      });
      return;
    }

    if (media.media_type === "image") {
      const img = new Image();
      img.style.cssText = mediaStyle(media);
      img.alt = media.name;
      img.src = src;
      container.appendChild(img);
      return;
    }

    if (media.media_type === "video") {
      const v = document.createElement("video");
      v.src = src;
      v.autoplay = true;
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.style.cssText = mediaStyle(media);
      container.appendChild(v);
      return;
    }

    // Fallback (audio / document non-PDF / unknown)
    container.innerHTML = `
      <div style="display:flex; align-items:center; justify-content:center; height:100%; color:#888; font-size:11px; font-family:system-ui; text-align:center; padding:8px;">
        ${escapeHtml(media.name)} (${media.media_type} belum didukung di zone)
      </div>
    `;
  })();

  return () => {
    cancelled = true;
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    if (pdfCleanup) pdfCleanup();
    container.innerHTML = "";
  };
}

/**
 * Mini cycler untuk playlist di dalam zone. Ganti item per duration, looping.
 * Bisa nesting (media render reuse renderZoneMedia).
 */
function renderZonePlaylist(
  container: HTMLElement,
  playlist: ManifestPlaylistData,
): () => void {
  let cancelled = false;
  let currentCleanup: (() => void) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let itemIndex = 0;

  const cycle = () => {
    if (cancelled) return;
    if (playlist.items.length === 0) {
      container.innerHTML = `
        <div style="display:flex; align-items:center; justify-content:center; height:100%; color:#666; font-size:12px; font-family:system-ui;">
          Playlist kosong
        </div>
      `;
      return;
    }
    if (currentCleanup) {
      try {
        currentCleanup();
      } catch {
        /* ignore */
      }
      currentCleanup = null;
    }
    container.innerHTML = "";

    // Cari item available mulai dari itemIndex (maks 1 putaran).
    let pick: ManifestPlaylistItem | null = null;
    for (let i = 0; i < playlist.items.length; i++) {
      const cand = playlist.items[(itemIndex + i) % playlist.items.length]!;
      if (!cand.media || isAvailableNow(cand.media)) {
        itemIndex = (itemIndex + i) % playlist.items.length;
        pick = cand;
        break;
      }
    }
    // Semua di luar window → kosongkan zona, cek ulang 60 detik.
    if (!pick) {
      container.innerHTML = "";
      timer = setTimeout(cycle, 60000);
      return;
    }
    const item = pick;
    if (item.media) {
      currentCleanup = renderZoneMedia(container, item.media);
    } else {
      container.innerHTML = `
        <div style="display:flex; align-items:center; justify-content:center; height:100%; color:#666; font-size:12px; font-family:system-ui;">
          Item tidak didukung di zone (cuma media)
        </div>
      `;
    }
    timer = setTimeout(() => {
      itemIndex++;
      cycle();
    }, Math.max(1000, item.duration * 1000));
  };

  cycle();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    if (currentCleanup) currentCleanup();
    container.innerHTML = "";
  };
}

// =========================================================================
// Screenshot capture — snap konten yang sedang ditampilkan jadi JPEG base64.
// =========================================================================
//
// Strategi per jenis child element di #player-root:
//   - <img>: draw ke canvas, JPEG (memerlukan source same-origin atau CORS).
//   - <video>: draw video.currentTime ke canvas, JPEG.
//   - <iframe> (YouTube/url): CORS-blocked, return placeholder text dengan
//     nama media supaya dashboard tetap nunjukin "ada konten iframe".
//   - <div.player-placeholder>: render text-only canvas dari isi placeholder.
//
// Output: data URL string ("data:image/jpeg;base64,...") atau null kalau gagal.

const SCREENSHOT_MAX_WIDTH = 960;

export async function captureScreenshot(): Promise<string | null> {
  if (!rootEl) return null;
  const child = rootEl.firstElementChild as HTMLElement | null;
  if (!child) return null;

  try {
    if (child instanceof HTMLImageElement) {
      return drawElementToJpeg(child, child.naturalWidth, child.naturalHeight);
    }
    if (child instanceof HTMLVideoElement) {
      return drawElementToJpeg(child, child.videoWidth, child.videoHeight);
    }
    if (child instanceof HTMLIFrameElement) {
      const label = state.manifest?.type === "media"
        ? state.manifest.media.name
        : "Konten iframe (live)";
      return renderTextPlaceholderJpeg("Iframe / URL", label);
    }
    // Placeholder div: ambil text content.
    const heading = child.querySelector("h1")?.textContent ?? "Player";
    const sub = child.querySelector("p")?.textContent ?? "";
    return renderTextPlaceholderJpeg(heading, sub);
  } catch (err) {
    console.warn("[screenshot] capture failed:", err);
    return null;
  }
}

function drawElementToJpeg(
  el: HTMLImageElement | HTMLVideoElement,
  intrinsicW: number,
  intrinsicH: number,
): string | null {
  const w = intrinsicW || el.clientWidth || 1280;
  const h = intrinsicH || el.clientHeight || 720;
  const scale = Math.min(1, SCREENSHOT_MAX_WIDTH / w);
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, cw, ch);
  ctx.drawImage(el, 0, 0, cw, ch);
  try {
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch {
    // Tainted canvas (cross-origin media tanpa CORS). Return placeholder.
    return null;
  }
}

function renderTextPlaceholderJpeg(title: string, sub: string): string {
  const canvas = document.createElement("canvas");
  canvas.width = 960;
  canvas.height = 540;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, 960, 540);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 48px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(title, 480, 260);
  ctx.fillStyle = "#999";
  ctx.font = "24px system-ui, sans-serif";
  ctx.fillText(sub.length > 60 ? sub.slice(0, 57) + "..." : sub, 480, 310);
  return canvas.toDataURL("image/jpeg", 0.7);
}

// =========================================================================
// Helpers
// =========================================================================

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}
